import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import type { AssetContainer } from '@babylonjs/core/assetContainer';
import type { Scene } from '@babylonjs/core/scene';
import type { Nullable } from '@babylonjs/core/types';
import type { ISurfaceMaterial } from '@seddi/surface-material';
import { createColor3, textureSetScale } from '@seddi/ui-babylon-core';
import type { TextureLoader } from './textureLoader';

export class SurfaceMaterialLoader {
  /** Babylon scene */
  private readonly _babylonScene: Scene;
  /** Texture loader. */
  private readonly _textureLoader: TextureLoader;

  constructor(babylonScene: Scene, textureLoader: TextureLoader) {
    this._babylonScene = babylonScene;
    this._textureLoader = textureLoader;
  }

  /**
   * Loads a surface material.
   * @param data - Surface material data.
   */
  loadAsync(data: ISurfaceMaterial): Promise<PBRMaterial> {
    return this._loadAsync(null, data);
  }

  /**
   * Loads a surface material with asset container.
   * @param container - Babylon asset container.
   * @param data - Surface material data.
   */
  async loadAssetContainerAsync(
    scene: Scene,
    container: Nullable<AssetContainer>,
    data: ISurfaceMaterial,
  ) {
    return this._loadAsync(container, data);
  }

  /**
   * Loads a surface material.
   * @param container - Babylon asset container.
   * @param data - Surface material to load.
   */
  async _loadAsync(
    container: Nullable<AssetContainer>,
    data: ISurfaceMaterial,
  ): Promise<PBRMaterial> {
    const { id, params, repeat } = data;

    this._babylonScene._blockEntityCollection = Boolean(container);
    const babylonMaterial = new PBRMaterial(id, this._babylonScene);
    babylonMaterial._parentContainer = container;
    this._babylonScene._blockEntityCollection = false;

    // TODO: Resolve all promises together
    // const promises = new Array<Promise<any>>();

    const {
      alpha,
      alphaMap,
      baseColor,
      baseColorMap,
      metallic,
      metallicMap,
      normalMap,
      roughness,
      roughnessMap,
      specular,
      specularMap,
    } = params;

    if (baseColorMap) {
      const texture = await this._textureLoader.loadAssetContainerAsync(container, baseColorMap);
      repeat && textureSetScale(texture, repeat);
      babylonMaterial.albedoTexture = texture;
    } else if (baseColor) {
      babylonMaterial.albedoColor = createColor3(baseColor).toLinearSpace();
    }
    if (alphaMap) {
      // TODO: pack alpha texture or use a Node material.
      // const texture = await this._textureLoader.loadAssetContainerAsync(scene, container, alphaMap);
      // textureSetScale(texture, repeat);
      // babylonMaterial.albedoTexture.a = texture;
      // babylonMaterial.albedoTexture.hasAlpha = true;
    }
    if (metallicMap) {
      // TODO: pack metallic and roughness or use a Node material.
      // const texture = await this._textureLoader.loadAssetContainerAsync(scene, container, metallicMap);
      // textureSetScale(texture, repeat);
      // babylonMaterial.reflectivityTexture.r = texture;
    }
    if (roughnessMap) {
      // TODO: pack metallic and roughness or use a Node material.
      // const texture = await this._textureLoader.loadAssetContainerAsync(scene, container, roughnessMap);
      // textureSetScale(texture, repeat);
      // babylonMaterial.reflectivityTexture.g = texture;
    }
    // TODO: metallic/roughness texture
    babylonMaterial.metallic = 0;
    babylonMaterial.roughness = 0.8;
    if (normalMap) {
      const texture = await this._textureLoader.loadAssetContainerAsync(container, normalMap);
      repeat && textureSetScale(texture, repeat);
      babylonMaterial.bumpTexture = texture;
    }
    if (specularMap) {
      const texture = await this._textureLoader.loadAssetContainerAsync(container, specularMap);
      repeat && textureSetScale(texture, repeat);
      babylonMaterial.reflectanceTexture = texture;
    }

    if (!alphaMap && alpha !== undefined) {
      babylonMaterial.alpha = alpha;
    }
    if (!metallicMap && metallic !== undefined) {
      babylonMaterial.metallic = metallic;
    }
    if (!roughnessMap && roughness !== undefined) {
      babylonMaterial.roughness = roughness;
    }
    if (!specularMap && specular !== undefined) {
      // _metallicF0Factor multiplies F0 = 0.04 which is equivalent to 0.5 values in specular map.
      babylonMaterial._metallicF0Factor = specular * 2;
    }

    return babylonMaterial;
  }
}
